import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';

// Automatic credit top-up — Settings → Credits → Credit Bundles.
// "When the team's balance drops below X, buy Y on the card on file."
// Only the account owner (solo user or team admin) can change it; other
// team members see what is set. Backend: /api/credits/auto-topup.

interface Pack { credits: number; price: number; label: string; priceLabel: string }
interface Settings {
  enabled: boolean;
  threshold: number;
  credits: number;
  last_topup_at: string | null;
  disabled_reason: string | null;
  disabled_at: string | null;
}
interface TopupView {
  unavailable?: boolean;
  canManage: boolean;
  isTeam: boolean;
  settings: Settings | null;
  thresholds: number[];
  packs: Pack[];
  billing: { name: string; hasCard: boolean; reason: string | null } | null;
  events: Array<{ id: number; status: string; credits: number; price_pence: number; created_at: string; error: string | null }>;
}

const fmtDate = (s: string | null) => s
  ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  : '';

const AutoTopupPanel: React.FC<{ onChanged?: () => void }> = ({ onChanged }) => {
  const { token } = useAuth();
  const [view, setView] = useState<TopupView | null>(null);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState(250);
  const [credits, setCredits] = useState(1000);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/credits/auto-topup', { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data: TopupView = await res.json();
      setView(data);
      if (data.settings) {
        setEnabled(data.settings.enabled);
        setThreshold(data.settings.threshold);
        setCredits(data.settings.credits);
      } else if (data.packs?.length) {
        setCredits(data.packs[0].credits);
      }
    } catch (e) {
      console.error('auto top-up load failed', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async (next: { enabled: boolean; threshold: number; credits: number }) => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const res = await fetch('/api/credits/auto-topup', {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not save');
        return false;
      }
      setView(v => v ? { ...v, settings: data.settings } : v);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onChanged?.();
      return true;
    } catch (e) {
      setError('Could not save. Please try again.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (loading || !view || view.unavailable) return null;

  const s = view.settings;
  const pack = view.packs.find(p => p.credits === credits);
  const dirty = !s || s.enabled !== enabled || s.threshold !== threshold || s.credits !== credits;
  const lastPaid = view.events.find(e => e.status === 'paid');

  // Read-only view for team members who are not admins.
  if (!view.canManage) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 px-6 py-4 mb-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-gray-900">Automatic top-up</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {s?.enabled
                ? <>When the team's balance drops below <b>{s.threshold.toLocaleString()}</b>, {s.credits.toLocaleString()} credits are bought automatically. Managed by your team admin.</>
                : 'Off. Your team admin can switch it on in Settings → Credits.'}
            </p>
          </div>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s?.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
            {s?.enabled ? 'On' : 'Off'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 px-6 py-5 mb-4" data-tour="credits-auto-topup">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-gray-900">Automatic top-up</p>
          <p className="text-xs text-gray-500 mt-0.5 max-w-xl">
            Never run dry mid-search. When your {view.isTeam ? "team's " : ''}balance drops below the threshold we buy a pack on the card on file
            {view.billing?.name ? <> ({view.billing.name}'s subscription)</> : null}, email you the invoice, and the credits are valid for 6 months.
            At most one top-up a day.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer select-none shrink-0">
          <span className="text-xs text-gray-600">{enabled ? 'On' : 'Off'}</span>
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            disabled={saving || (!enabled && view.billing !== null && !view.billing.hasCard)}
            onClick={() => setEnabled(v => !v)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${enabled ? 'bg-green-500' : 'bg-gray-300'}`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </label>
      </div>

      {view.billing && !view.billing.hasCard && (
        <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
          {view.billing.reason || 'Automatic top-up needs a card on file.'}
        </div>
      )}

      {s?.disabled_reason && !s.enabled && (
        <div className="mt-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800">
          Switched off on {fmtDate(s.disabled_at)}: {s.disabled_reason} Nothing was charged.
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-end gap-4">
        <label className="text-xs text-gray-600">
          <span className="block mb-1">When balance drops below</span>
          <select
            value={threshold}
            onChange={e => setThreshold(parseInt(e.target.value, 10))}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm text-gray-900 bg-white"
          >
            {view.thresholds.map(t => <option key={t} value={t}>{t.toLocaleString()} credits</option>)}
          </select>
        </label>
        <label className="text-xs text-gray-600">
          <span className="block mb-1">Buy</span>
          <select
            value={credits}
            onChange={e => setCredits(parseInt(e.target.value, 10))}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm text-gray-900 bg-white"
          >
            {view.packs.map(p => <option key={p.credits} value={p.credits}>{p.label} — {p.priceLabel}</option>)}
          </select>
        </label>
        <button
          type="button"
          disabled={saving || !dirty}
          onClick={() => save({ enabled, threshold, credits })}
          className="px-4 py-1.5 rounded-md text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {saved && <span className="text-xs text-green-700">Saved</span>}
        {error && <span className="text-xs text-red-700">{error}</span>}
      </div>

      {pack && (
        <p className="mt-2 text-xs text-gray-500">
          {enabled
            ? <>Each top-up charges <b>{pack.priceLabel}</b> plus any VAT and adds {pack.credits.toLocaleString()} credits.</>
            : 'Automatic top-up is off. Nothing is charged until you switch it on and save.'}
          {lastPaid && <> Last top-up {fmtDate(lastPaid.created_at)}.</>}
        </p>
      )}
    </div>
  );
};

export default AutoTopupPanel;
